import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";

function Profile() {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  // Format Firestore Timestamp / string / millis / {seconds,nanos} / or fall back to Auth metadata
  function formatCreatedAt(createdAt) {
    try {
      // Prefer Firestore value if present
      if (createdAt) {
        // Firestore Timestamp
        if (typeof createdAt?.toDate === "function") {
          const d = createdAt.toDate();
          if (!isNaN(d)) {
            return d.toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "2-digit",
            });
          }
        }
        // Seconds/nanos object
        if (typeof createdAt?.seconds === "number") {
          const d = new Date(createdAt.seconds * 1000);
          if (!isNaN(d)) {
            return d.toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "2-digit",
            });
          }
        }
        // Millis number
        if (typeof createdAt === "number") {
          const d = new Date(createdAt);
          if (!isNaN(d)) {
            return d.toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "2-digit",
            });
          }
        }
        // String stored in Firestore (your current legacy value)
        if (typeof createdAt === "string") {
          return createdAt; // display as-is
        }
      }

      // Fallback: Auth user creation time (ISO string)
      const authCreation = auth.currentUser?.metadata?.creationTime;
      if (authCreation) {
        const d = new Date(authCreation);
        if (!isNaN(d)) {
          return d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
          });
        }
      }

      return "Unavailable";
    } catch {
      return "Unavailable";
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        console.warn("No authenticated user found");
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserInfo({
            name:
              data.firstName && data.lastName
                ? `${data.firstName} ${data.lastName}`
                : "(not set)",
            username: data.username,
            email: data.email || user.email,
            uid: user.uid,
            credits: data.credits ?? 0,
            createdAt: data.createdAt ?? null, // may be Timestamp or string
          });
        } else {
          console.warn("User document not found in Firestore");
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const copyUid = async () => {
    if (!userInfo?.uid) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(userInfo.uid);
      } else {
        // Fallback for very old browsers
        const tmp = document.createElement("textarea");
        tmp.value = userInfo.uid;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand("copy");
        document.body.removeChild(tmp);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      console.error("Failed to copy UID:", e);
    }
  };

  return (
    <DashboardLayout>
      {/* Mobile-only Blue Back Button */}
      <div className="sm:hidden flex justify-start px-6 pt-6">
        <button
          onClick={() => navigate("/user-dashboard")}
          className="bg-[#1976d2] text-white text-sm font-bold font-[Montserrat] px-5 py-2 rounded-md"
        >
          ← Back
        </button>
      </div>

      <div className="p-8 text-white flex flex-col items-center">
        <h2 className="text-3xl font-bold mb-8">User Profile</h2>

        <div className="bg-gray-700 bg-opacity-70 rounded-xl p-8 w-full max-w-xl text-left relative text-lg leading-relaxed sm:text-lg text-sm">
          {loading ? (
            <p>Loading user info...</p>
          ) : userInfo ? (
            <div>
              <p className="mb-3">
                <strong>Name:</strong> {userInfo.name}
              </p>
              <p className="mb-3">
                <strong>Username:</strong> {userInfo.username}
              </p>
              <p className="mb-3">
                <strong>Email:</strong> {userInfo.email}
              </p>

              {/* Support ID + Copy button on the same line */}
              <p className="mb-3 flex items-center gap-2">
                <strong className="whitespace-nowrap">Support ID:</strong>
                <span className="font-mono text-sm px-2 py-1 rounded bg-black/40 whitespace-nowrap overflow-x-auto max-w-[220px] inline-block">
                  {userInfo.uid}
                </span>
                <button
                  type="button"
                  onClick={copyUid}
                  className="ml-1 text-sm px-2 py-1 rounded bg-gray-600 hover:bg-gray-500 active:bg-gray-400 transition-colors whitespace-nowrap"
                  aria-label="Copy user ID to clipboard"
                >
                  {copied ? "Copied!" : "(Copy ID)"}
                </button>
              </p>

              <p className="mb-3">
                <strong>Account Credits:</strong> {userInfo.credits}
              </p>

              <p className="text-sm text-center text-gray-300 mt-6">
                Account Created: {formatCreatedAt(userInfo.createdAt)}
              </p>
            </div>
          ) : (
            <p>User info could not be loaded.</p>
          )}
        </div>

        <button
          className="mt-8 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-lg"
          onClick={() => navigate("/purchase-history")}
        >
          Purchase History
        </button>
      </div>
    </DashboardLayout>
  );
}

export default Profile;
